import React from 'react';
import { ExternalLink, Loader, RefreshCw, Zap, Edit } from 'lucide-react';
import { connectivityService, ConnectivityResult } from '../../../lib/connectivityService';
import { pagespeedService } from '../../../lib/pagespeedService';
import { Prospect } from './types';

interface PageSpeedResult {
  url: string;
  performance_score: number;
  accessibility_score: number;
  best_practices_score: number;
  seo_score: number;
  mobile_performance_score?: number;
  desktop_performance_score?: number;
  desktop_data?: any;
  mobile_data?: any;
  prospect_id?: string;
}

interface ContactInfoCardProps {
  prospect: Prospect;
  connectivityResult: ConnectivityResult | null;
  setConnectivityResult: (result: ConnectivityResult | null) => void;
  setPagespeedResults: (results: PageSpeedResult | null) => void;
  isAnalyzing: boolean;
  setIsAnalyzing: (analyzing: boolean) => void;
  isCheckingConnectivity: boolean;
  setIsCheckingConnectivity: (checking: boolean) => void;
  onEdit?: () => void;
  onCheckConnectivity?: () => void;
}

const ContactInfoCard: React.FC<ContactInfoCardProps> = ({
  prospect,
  connectivityResult,
  setConnectivityResult,
  setPagespeedResults,
  isAnalyzing,
  setIsAnalyzing,
  isCheckingConnectivity,
  setIsCheckingConnectivity,
  onEdit,
  onCheckConnectivity
}) => {
  const handleConnectivityCheck = async () => {
    if (!prospect.site_url) return;
    
    setIsCheckingConnectivity(true);
    
    try {
      // Normaliser l'URL
      const urlToAnalyze = connectivityService.normalizeUrl(prospect.site_url);
      
      // Vérifier d'abord la connectivité
      const connectivity = await connectivityService.checkConnectivityWithFallback(urlToAnalyze);
      setConnectivityResult(connectivity);
      
    } catch (error) {
      console.error('Erreur lors de la vérification de la connectivité:', error);
      alert(`Erreur lors de la vérification de la connectivité: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    } finally {
      setIsCheckingConnectivity(false);
    }
  };

  const handlePageSpeedAnalysis = async () => {
    if (!prospect.site_url) return;
    
    setIsAnalyzing(true);
    
    try {
      // Normaliser l'URL
      const urlToAnalyze = connectivityService.normalizeUrl(prospect.site_url);
      
      // Si le site est accessible, procéder à l'analyse PageSpeed
      const results = await pagespeedService.analyzeSite(urlToAnalyze, prospect.id);
      if (results) {
        setPagespeedResults(results);
      }
      
    } catch (error) {
      console.error('Erreur lors de l\'analyse:', error);
      alert(`Erreur lors de l'analyse: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header avec informations de base et bouton d'édition pour tous les prospects */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          {prospect.prenom} {prospect.nom}
        </h3>
        {onEdit && (
          <button
            onClick={onEdit}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
          >
            <Edit className="w-4 h-4 mr-1" />
            Modifier
          </button>
        )}
      </div>

      {/* Informations de base en grid compact */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <dt className="text-sm font-medium text-gray-500">Email</dt>
          <dd className="mt-1 text-sm text-gray-900">{prospect.email || '-'}</dd>
        </div>
        
        <div>
          <dt className="text-sm font-medium text-gray-500">Téléphone</dt>
          <dd className="mt-1 text-sm text-gray-900">{prospect.metadata?.telephone || prospect.telephone || '-'}</dd>
        </div>
        
        <div className="md:col-span-2">
          <dt className="text-sm font-medium text-gray-500">LinkedIn</dt>
          <dd className="mt-1">
            {prospect.linkedin ? (
              <a
                href={prospect.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline break-all flex items-center"
              >
                {prospect.linkedin}
                <ExternalLink className="w-3 h-3 ml-1 flex-shrink-0" />
              </a>
            ) : (
              <span className="text-sm text-gray-500">-</span>
            )}
          </dd>
        </div>

        <div>
          <dt className="text-sm font-medium text-gray-500">Source</dt>
          <dd className="mt-1 text-sm text-gray-900">{prospect.source || '-'}</dd>
        </div>

        <div>
          <dt className="text-sm font-medium text-gray-500">Poste</dt>
          <dd className="mt-1 text-sm text-gray-900">{prospect.poste || '-'}</dd>
        </div>

        <div>
          <dt className="text-sm font-medium text-gray-500">Titre</dt>
          <dd className="mt-1 text-sm text-gray-900">{prospect.metadata?.title || '-'}</dd>
        </div>

        <div>
          <dt className="text-sm font-medium text-gray-500">Entreprise</dt>
          <dd className="mt-1 text-sm text-gray-900">{prospect.metadata?.company || '-'}</dd>
        </div>

        <div>
          <dt className="text-sm font-medium text-gray-500">Secteur d'activité</dt>
          <dd className="mt-1 text-sm text-gray-900">{prospect.metadata?.industry || '-'}</dd>
        </div>

        <div>
          <dt className="text-sm font-medium text-gray-500">Localisation</dt>
          <dd className="mt-1 text-sm text-gray-900">{prospect.metadata?.location || '-'}</dd>
        </div>

        <div>
          <dt className="text-sm font-medium text-gray-500">Taille entreprise</dt>
          <dd className="mt-1 text-sm text-gray-900">{prospect.metadata?.companySize || '-'}</dd>
        </div>

        <div>
          <dt className="text-sm font-medium text-gray-500">Localisation entreprise</dt>
          <dd className="mt-1 text-sm text-gray-900">{prospect.metadata?.companyLocation || '-'}</dd>
        </div>

        <div>
          <dt className="text-sm font-medium text-gray-500">Domaine Emelia</dt>
          <dd className="mt-1 text-sm text-gray-900">{prospect.metadata?.domain_emelia || '-'}</dd>
        </div>

        <div>
          <dt className="text-sm font-medium text-gray-500">Revenu estimé</dt>
          <dd className="mt-1 text-sm text-gray-900">
            {prospect.metadata?.estimatedMinRevenue && prospect.metadata?.estimatedMaxRevenue 
              ? `${prospect.metadata.estimatedMinRevenue} - ${prospect.metadata.estimatedMaxRevenue}`
              : '-'}
          </dd>
        </div>
      </div>

      {/* Sections de texte long */}
      <div className="border-t pt-4">
        <dt className="text-sm font-medium text-gray-500 mb-2">Résumé</dt>
        <dd className="text-sm text-gray-900">{prospect.metadata?.summary || '-'}</dd>
      </div>

      <div className="border-t pt-4">
        <dt className="text-sm font-medium text-gray-500 mb-2">Description</dt>
        <dd className="text-sm text-gray-900">{prospect.metadata?.description || '-'}</dd>
      </div>

      <div className="border-t pt-4">
        <dt className="text-sm font-medium text-gray-500 mb-2">Description entreprise</dt>
        <dd className="text-sm text-gray-900">{prospect.metadata?.companyDescription || '-'}</dd>
      </div>

      <div className="border-t pt-4">
        <dt className="text-sm font-medium text-gray-500 mb-2">Notes</dt>
        <dd className="text-sm text-gray-900 whitespace-pre-wrap">{prospect.notes || '-'}</dd>
      </div>

      {/* Site web avec analyse */}
      <div className="border-t pt-4">
        <div className="flex items-center justify-between mb-3">
          <dt className="text-sm font-medium text-gray-500">Site web</dt>
          {prospect.site_url && (
            <div className="flex items-center space-x-2">
              {connectivityResult && (
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  connectivityResult.isAccessible 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {connectivityResult.isAccessible ? 'Accessible' : 'Inaccessible'}
                </span>
              )}
              <button
                onClick={onCheckConnectivity || handleConnectivityCheck}
                disabled={isCheckingConnectivity}
                className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
              >
                {isCheckingConnectivity ? (
                  <Loader className="w-3 h-3 animate-spin" />
                ) : (
                  <RefreshCw className="w-3 h-3" />
                )}
              </button>
            </div>
          )}
        </div>
        <dd className="flex items-center justify-between">
          {prospect.site_url ? (
            <a
              href={prospect.site_url.startsWith('http') ? prospect.site_url : `https://${prospect.site_url}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:text-blue-800 hover:underline break-all flex items-center"
            >
              {prospect.site_url}
              <ExternalLink className="w-3 h-3 ml-1 flex-shrink-0" />
            </a>
          ) : (
            <span className="text-sm text-gray-500">-</span>
          )}
        </dd>
        
        {/* Bouton d'analyse PageSpeed compact */}
        <div className="mt-3">
          <button
            onClick={handlePageSpeedAnalysis}
            disabled={isAnalyzing}
            className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isAnalyzing ? (
              <>
                <Loader className="w-3 h-3 mr-1 animate-spin" />
                Analyse en cours...
              </>
            ) : (
              <>
                <Zap className="w-3 h-3 mr-1" />
                Analyser PageSpeed
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContactInfoCard;